import React, { useState, useEffect } from 'react';
import { registrationRequestService } from '../../services/api';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';


const RequestManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionInProgress, setActionInProgress] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Carica le richieste di registrazione
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await registrationRequestService.getRequests();
        // Mostra solo le richieste in stato "pending" per evitare errori 400 su richieste già elaborate
        const pending = Array.isArray(response.data)
          ? response.data.filter((r) => r.status === 'pending')
          : [];
        setRequests(pending);
        setLoading(false);
      } catch (err) {
        setError('Errore nel caricamento delle richieste');
        setLoading(false);
        console.error(err);
      }
    };

    fetchRequests();
  }, []);

  // Gestisce l'approvazione di una richiesta
  const handleApprove = async (id) => {
    setActionInProgress(id);
    setError('');
    setSuccessMessage('');
    
    try {
      await registrationRequestService.approveRequest(id);
      setRequests(requests.filter(request => request.id !== id));
      setSuccessMessage('Richiesta approvata con successo. L\'utente riceverà le credenziali via email.');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      setError(detail || 'Errore nell\'approvazione della richiesta');
      console.error(err);
    }
    
    setActionInProgress(null);
  };

  // Gestisce il rifiuto di una richiesta
  const handleReject = async (id) => {
    setActionInProgress(id);
    setError('');
    setSuccessMessage('');
    
    try {
      await registrationRequestService.rejectRequest(id);
      setRequests(requests.filter(request => request.id !== id));
      setSuccessMessage('Richiesta rifiutata con successo.');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      setError(detail || 'Errore nel rifiuto della richiesta');
      console.error(err);
    }
    
    setActionInProgress(null);
  };

  // Formatta la data
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('it-IT', options);
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth={false} sx={{ px: { xs: 1.5, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: 4, mt: 8, width: '100%', maxWidth: 1600, mx: 'auto' }}>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'center', position: 'relative', mb: 3 }} >
          <Button  component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" color="primary"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' }, mb: { xs: 1, sm: 0 }, textTransform: 'none', }} >
            Dashboard
          </Button>

          <Typography component="h1" variant="h4" gutterBottom
            sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%', }} >
            Richiesta di Registrazione
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        
        {requests.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ mt: 4 }}>
            Non ci sono richieste di registrazione in attesa.
          </Typography>
        ) : !isMobile ? (
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '8%' }}>Nome</TableCell>
                  <TableCell sx={{ width: '8%' }}>Cognome</TableCell>
                  <TableCell sx={{ width: '16%' }}>Email</TableCell>
                  <TableCell sx={{ width: '9%' }}>Cellulare</TableCell>
                  <TableCell sx={{ width: '8%' }}>Città</TableCell>
                  <TableCell sx={{ width: '12%' }}>Indirizzo</TableCell>
                  <TableCell sx={{ width: '6%' }}>N. Civico</TableCell>
                  <TableCell sx={{ width: '11%' }}>Codice Fiscale</TableCell>
                  <TableCell sx={{ width: '12%' }}>Data Richiesta</TableCell>
                  <TableCell sx={{ width: '18%' }} align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.first_name}</TableCell>
                    <TableCell>{request.last_name}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{request.email}</TableCell>
                    <TableCell>{request.phone_number}</TableCell>
                    <TableCell>{request.city}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{request.address}</TableCell>
                    <TableCell>{request.street_number}</TableCell>
                    <TableCell>{request.fiscal_code}</TableCell>
                    <TableCell>{formatDate(request.created_at)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleApprove(request.id)}
                          disabled={actionInProgress === request.id}
                        >
                          {actionInProgress === request.id ? <CircularProgress size={24} /> : 'Approva'}
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleReject(request.id)}
                          disabled={actionInProgress === request.id}
                        >
                          Rifiuta
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {requests.map((request) => (
              <Paper key={request.id} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {request.first_name} {request.last_name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {request.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {request.phone_number || '-'}
                </Typography>
                <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Città: {request.city || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Indirizzo: {request.address || '-'} {request.street_number || ''}</Typography>
                  <Typography variant="caption" color="text.secondary">Codice fiscale: {request.fiscal_code || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Data richiesta: {formatDate(request.created_at)}</Typography>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleApprove(request.id)}
                    disabled={actionInProgress === request.id}
                  >
                    {actionInProgress === request.id ? <CircularProgress size={20} /> : 'Approva'}
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleReject(request.id)}
                    disabled={actionInProgress === request.id}
                  >
                    Rifiuta
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RequestManagement;
